import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Link,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import { LockOutlined, PersonAdd, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: ''
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);

  const { login, register, error } = useAuth();
  const { t } = useTranslation();


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setAttemptsLeft(null);
    setLockedUntil(null);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        // Validate password confirmation
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }

        const result = await register({
          email,
          password,
          firstName: formData.firstName,
          lastName: formData.lastName
          // department, position, and employeeId will be set by admins
        });
        
        if (result.needsApproval) {
          setRegistrationSuccess(true);
        }
        // If not needsApproval, user is already logged in (admin case)
      }
    } catch (error: any) {
      console.error('Authentication error:', error);
      
      // Handle lockout and attempt information from server response
      if (error.response?.data?.attemptsLeft !== undefined) {
        setAttemptsLeft(error.response.data.attemptsLeft);
      }
      if (error.response?.data?.lockedUntil) {
        setLockedUntil(error.response.data.lockedUntil);
      }
      
      // Note: Error messages are handled by AuthContext and displayed via the 'error' prop from useAuth()
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'email') {
      setEmail(value);
    } else if (field === 'password') {
      setPassword(value);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFormData({
      firstName: '',
      lastName: ''
    });
    setRegistrationSuccess(false);
    setAttemptsLeft(null);
    setLockedUntil(null);
  };

  if (registrationSuccess) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'success.main',
                borderRadius: '50%',
                width: 56,
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 2,
              }}
            >
              <PersonAdd sx={{ color: 'white', fontSize: 28 }} />
            </Box>

            <Typography component="h1" variant="h4" gutterBottom>
              {t('auth.registrationSuccess')}
            </Typography>
            
            <Alert severity="info" sx={{ width: '100%', mb: 3 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t('auth.accountCreatedSuccess')}
              </Typography>
              <Typography variant="body2">
                {t('auth.pendingApproval')}
              </Typography>
            </Alert>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setIsLogin(true);
                resetForm();
              }}
              sx={{ py: 1.5 }}
            >
              {t('auth.backToLogin')}
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: 2,
            position: 'relative',
          }}
        >
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
            <LanguageSwitcher />
          </Box>
          <Box
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 2,
            }}
          >
            {isLogin ? (
              <LockOutlined sx={{ color: 'white', fontSize: 28 }} />
            ) : (
              <PersonAdd sx={{ color: 'white', fontSize: 28 }} />
            )}
          </Box>

          <Typography component="h1" variant="h4" gutterBottom>
            Mokslo Sala
          </Typography>
          
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {isLogin ? t('auth.loginSubtitle') : t('auth.registerSubtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error === 'Invalid credentials' ? t('auth.invalidCredentials') : error}
            </Alert>
          )}

          {/* Lockout warning */}
          {lockedUntil && (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              <Typography variant="body2">
                Paskyra užrakinta dėl daugybės nesėkmingų prisijungimo bandymų.
                Bandykite dar kartą po 30 minučių.
              </Typography>
            </Alert>
          )}

          {/* Attempts remaining */}
          {attemptsLeft !== null && attemptsLeft > 0 && (
            <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
              <Typography variant="body2">
                Liko {attemptsLeft} {attemptsLeft === 1 ? 'bandymas' : 'bandymai'} prieš paskyros užrakinimą.
              </Typography>
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {!isLogin && (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Vardas"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  autoFocus
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Pavardė"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                />
              </>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              label="El. paštas"
              type="email"
              value={email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              autoComplete="email"
            />

            <TextField
              margin="normal"
              required
              fullWidth
              label="Slaptažodis"
              type={!isLogin && showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              autoComplete={isLogin ? "current-password" : "new-password"}
              InputProps={!isLogin ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword(prev => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              } : undefined}
            />

            {!isLogin && (
              <TextField
                margin="normal"
                required
                fullWidth
                label="Slaptažodis (patvirtinimas)"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                error={confirmPassword !== '' && password !== confirmPassword}
                helperText={confirmPassword !== '' && password !== confirmPassword ? 'Slaptažodžiai nesutampa' : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowConfirmPassword(prev => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading || (isLogin && lockedUntil !== null)}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                isLogin ? t('auth.loginButton') : t('auth.createAccount')
              )}
            </Button>

            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('auth.or')}
              </Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setIsLogin(!isLogin);
                resetForm();
              }}
              sx={{ py: 1.5 }}
            >
              {isLogin ? t('auth.createNewAccount') : t('auth.alreadyHaveAccount')}
            </Button>
          </Box>

          {/* Demo account notice removed */}
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage; 